import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { TranslateModule } from '@ngx-translate/core';
import { Dossier, Fitter } from '@sc/data-access-models';
import { LocaleDatePipe } from '@sc/data-access-translation';
import { ButtonComponent } from '@sc/ui-components';
import { dayjs, groupByDateAndSort } from '@sc/utils';
import { PlanningBasketAppointmentComponent } from '../planning-basket-appointment/planning-basket-appointment.component';

@Component({
  selector: 'sc-planning-basket',
  styleUrls: ['./planning-basket.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    MatExpansionModule,
    NgFor,
    PlanningBasketAppointmentComponent,
    ButtonComponent,
    AsyncPipe,
    TranslateModule,
    LocaleDatePipe,
  ],
  template: `
    <ng-container *ngIf="requestedApmtDossiers?.length > 0">
      <h1>
        {{
          'PlanningBasket.requestedAppointmentsTitle'
            | translate: { nbRemaining: requestedApmtDossiers?.length }
        }}
      </h1>
      <mat-accordion [multi]="true">
        <mat-expansion-panel [expanded]="true">
          <mat-expansion-panel-header>
            <mat-panel-title>
              <span>
                {{ 'PlanningBasket.notProgrammedHeader' | translate }}
              </span>
            </mat-panel-title>
          </mat-expansion-panel-header>
          <div class="appointment-container">
            <sc-planning-basket-appointment
              class="appointment-card"
              *ngFor="let dossier of requestedApmtDossiers"
              [dossier]="dossier"
              [fitter]="getDossierFitter(dossier)"
              (validateDossier)="validateExternalDossierEvent.emit(dossier)"
            >
            </sc-planning-basket-appointment>
          </div>
        </mat-expansion-panel>
      </mat-accordion>
    </ng-container>
    <h1 class="margin-not-first">
      {{
        'PlanningBasket.title'
          | translate: { nbRemaining: _externalDossiers?.length }
      }}
    </h1>
    <sc-button
      *ngIf="_externalDossiers?.length > 0"
      id="saveAllDossier"
      leftIcon="check_bis"
      buttonType="primary"
      (click)="saveExternalDossierByDayEvent.emit(_externalDossiers)"
      >{{ 'PlanningBasketAppointment.saveAllBtn' | translate }}</sc-button
    >
    <mat-accordion class="group-by-days" [multi]="true">
      <mat-expansion-panel
        *ngFor="let appointmentsDay of getAllAvailableDays(); let i = index"
        [expanded]="isExpandedByDefault(appointmentsDay)"
      >
        <sc-button
          id="saveDayDossier"
          leftIcon="check_bis"
          buttonType="mainWhite"
          (click)="
            saveExternalDossierByDayEvent.emit(
              externalDossiersGroupByDays.get(appointmentsDay)
            )
          "
          >{{ 'PlanningBasketAppointment.saveDayBtn' | translate }}</sc-button
        >
        <mat-expansion-panel-header>
          <mat-panel-title>
            <span class="appointment-day">
              {{ appointmentsDay | localeDate: 'EEEE dd MMMM' | async }} ({{
                externalDossiersGroupByDays.get(appointmentsDay)?.length
              }})
            </span>
          </mat-panel-title>
        </mat-expansion-panel-header>
        <div class="appointment-container">
          <sc-planning-basket-appointment
            class="appointment-card"
            *ngFor="
              let dossier of externalDossiersGroupByDays.get(appointmentsDay)
            "
            [dossier]="dossier"
            [fitter]="getDossierFitter(dossier)"
            (validateDossier)="validateExternalDossierEvent.emit(dossier)"
            (saveDossier)="saveExternalDossierEvent.emit(dossier)"
          >
          </sc-planning-basket-appointment>
        </div>
      </mat-expansion-panel>
    </mat-accordion>
  `,
})
export class PlanningBasketComponent {
  groupByDateAndSort = groupByDateAndSort;
  @Input() set externalDossiers(dossiers) {
    this._externalDossiers = dossiers ?? [];
    this.externalDossiersGroupByDays = this.groupByDateAndSort(
      this._externalDossiers
    );
    this.changeDetectorRef.markForCheck();
  }
  _externalDossiers: Dossier[];

  @Input() requestedApmtDossiers: Dossier[];

  @Input() fitters: Fitter[] = [];
  @Input() calendarDay: string = dayjs().format();

  @Output()
  validateExternalDossierEvent: EventEmitter<Dossier> = new EventEmitter<Dossier>();

  @Output()
  saveExternalDossierEvent: EventEmitter<Dossier> = new EventEmitter<Dossier>();

  @Output()
  saveExternalDossierByDayEvent: EventEmitter<Dossier[]> = new EventEmitter<
    Dossier[]
  >();

  externalDossiersGroupByDays: Map<string, Dossier[]>;

  constructor(private readonly changeDetectorRef: ChangeDetectorRef) {}

  getAllAvailableDays() {
    return Array.from(this.externalDossiersGroupByDays?.keys() || []);
  }

  isExpandedByDefault(appointmentsDay: string): boolean {
    return dayjs(appointmentsDay, 'YYYY-MM-DD').isSame(this.calendarDay, 'day');
  }

  getDossierFitter(dossier: Dossier) {
    return this.fitters.find(
      (fitter) =>
        fitter.id ===
        (dossier.appointment?.fitterId ?? dossier.requestAppointment?.fitterId)
    );
  }
}
